import * as React from 'react';
import type { IdentityOcr, IdentityOcrResult } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { ApiHatasi, apiForm } from '@/lib/api';
import { kucultVeJpeg, videodanKare } from '@/lib/gorsel';
import { useKamera } from '@/lib/kamera';

/**
 * "Arka yuzu tamamla" paneli.
 *
 * Iki adimli akis arka yuzu zaten zorunlu kilar; bu panel ARTA KALAN durum
 * icindir: gorevli "arka yuzu yok" deyip atlamis, ya da verilen arka yuz
 * okunamamistir. O noktada anne adi ve baba adi bos kalir - ve bunlar KBS'de
 * ZORUNLUDUR.
 *
 * Alternatif "bastan oku" olurdu ama o, dogru okunmus on yuzu de cope atar.
 * Burada YALNIZCA arka yuz okunur ve sonuc BOS ALANLARA yazilir; dolu bir
 * alanin uzerine asla yazilmaz - gorevlinin elle duzelttigi bir deger
 * ikinci bir OCR tahminiyle bozulmamali.
 */

interface Props {
  reservationId?: string | null;
  onTamamlandi: (ek: IdentityOcr) => void;
}

export function ArkaYuzTamamla({ reservationId, onTamamlandi }: Props) {
  const kamera = useKamera();
  const [okuyor, setOkuyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  async function oku(gorsel: Blob) {
    setOkuyor(true);
    setHata(null);
    try {
      const form = new FormData();
      form.append('gorsel', gorsel, 'kimlik-arka.jpg');
      if (reservationId) form.append('reservationId', reservationId);
      const sonuc = await apiForm<IdentityOcrResult>('/api/ocr/identity', form);
      kamera.kapat();
      onTamamlandi(sonuc.alanlar);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Arka yüz okunamadı. Bilgileri elle girebilirsiniz.');
    } finally {
      setOkuyor(false);
    }
  }

  async function cek() {
    if (!kamera.videoRef.current || !kamera.acik) return;
    try {
      await oku(await videodanKare(kamera.videoRef.current));
    } catch {
      setHata('Kamera görüntüsü henüz hazır değil. Bir saniye bekleyip tekrar deneyin.');
    }
  }

  async function dosyaSecildi(olay: React.ChangeEvent<HTMLInputElement>) {
    const dosya = olay.target.files?.[0];
    olay.target.value = '';
    if (!dosya) return;
    try {
      await oku(await kucultVeJpeg(dosya));
    } catch {
      setHata('Bu dosya okunamadı. Fotoğrafı yeniden çekin ya da JPEG/PNG seçin.');
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
      <div>
        <p className="font-semibold text-amber-900">Belgenin arka yüzü okunmadı</p>
        <p className="text-[13px] text-amber-900/80">
          Anne adı, baba adı ve doğum yeri kimliğin arka yüzündedir; anne ve baba adı KBS bildirimi için
          zorunludur. Elle yazmak yerine arka yüzü okutun — yalnızca boş alanlar dolar.
        </p>
      </div>

      {/* <video> KOSULSUZ basilir; bkz. lib/kamera.ts */}
      <video
        ref={kamera.videoRef}
        autoPlay
        playsInline
        muted
        className={
          'aspect-[4/3] w-full rounded-lg bg-slate-900 object-cover sm:aspect-[16/10] ' +
          (kamera.acik ? '' : 'hidden')
        }
      />

      {(hata ?? kamera.hata) && <p className="text-[13px] text-red-700">{hata ?? kamera.hata}</p>}

      <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
        {kamera.acik ? (
          <Button onClick={cek} yukleniyor={okuyor} className="w-full sm:w-auto">
            Arka yüzü çek ve oku
          </Button>
        ) : (
          <Button onClick={kamera.ac} yukleniyor={kamera.aciliyor} className="w-full sm:w-auto">
            Arka yüz için kamerayı aç
          </Button>
        )}
        <Button
          varyant="ikincil"
          onClick={() => document.getElementById('dosya-arka-tamamla')?.click()}
          yukleniyor={okuyor}
          className="w-full sm:w-auto"
        >
          Dosya seç
        </Button>
        <input
          id="dosya-arka-tamamla"
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={dosyaSecildi}
        />
      </div>
    </div>
  );
}
